import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, SyncOutlined } from '@ant-design/icons';
import { MONDAY_SKU_DATABASE_BOARD_ID } from 'pages/dashboard/MondayIntegration/constants';
import * as api from 'util/api';

const { Text } = Typography;

function copyFormula(text) {
  if (!text) return;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => message.success('Copied — paste into the Monday column formula'))
      .catch(() => message.error('Copy failed'));
    return;
  }
  message.error('Clipboard not available');
}

const MondayFormulaCheck = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    try {
      const data = await api.checkMondayFormulas();
      setResult(data);
      if ((data?.conflictCount ?? 0) === 0) {
        message.success('Monday ✨ formulas match the Guide');
      } else {
        message.warning(`${data.conflictCount} Monday formula(s) out of date`);
      }
    } catch (err) {
      message.error(err?.message || 'Failed to check Monday formulas');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Column',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (title) => <Text strong>{title}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        if (status === 'match') return <Tag color="success">Match</Tag>;
        if (status === 'missing') return <Tag color="warning">Missing</Tag>;
        return <Tag color="error">Conflict</Tag>;
      },
    },
    {
      title: 'Correct formula',
      dataIndex: 'expected',
      key: 'expected',
      render: (expected, record) => {
        if (record.status === 'match') {
          return <Text type="secondary">In sync</Text>;
        }
        return (
          <div className="pricing-guide-monday-copy">
            <Input.TextArea
              readOnly
              value={expected}
              rows={4}
              className="pricing-guide-monday-formula"
              onFocus={(e) => e.target.select()}
            />
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyFormula(expected)}
            >
              Copy
            </Button>
          </div>
        );
      },
    },
  ];

  const extra = (
    <span className="pricing-guide-monday-extra">
      <a
        href={`https://garageproject.monday.com/boards/${MONDAY_SKU_DATABASE_BOARD_ID}`}
        target="_blank"
        rel="noreferrer"
      >
        SKU Board
      </a>
      <Button
        size="small"
        icon={<SyncOutlined spin={loading} />}
        loading={loading}
        onClick={runCheck}
      >
        Check Monday
      </Button>
    </span>
  );

  return (
    <Card
      size="small"
      title={(
        <Tooltip title="Default Guide targets only — Monday cannot encode range overrides">
          <span>Monday SKU formulas</span>
        </Tooltip>
      )}
      extra={extra}
      className="pricing-guide-section"
    >
      {!result && (
        <Text type="secondary">
          Compare Guide Default targets to ✨ suggestion columns on the SKU board.
          Conflicts show the formula to paste into that Monday column.
        </Text>
      )}
      {result && (
        <>
          {(result.conflictCount ?? 0) === 0 ? (
            <Alert
              type="success"
              showIcon
              className="pricing-guide-monday-alert"
              message="All ✨ formulas match the Guide"
            />
          ) : (
            <Alert
              type="warning"
              showIcon
              className="pricing-guide-monday-alert"
              message={`${result.conflictCount} column(s) differ — copy the formula into Monday`}
            />
          )}
          <Table
            size="middle"
            pagination={false}
            rowKey="id"
            dataSource={result.columns || []}
            columns={columns}
          />
        </>
      )}
    </Card>
  );
};

export default MondayFormulaCheck;
